import { useEffect, useRef, useState } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { RecipientChip } from './RecipientChip';
import type { EmailComposerContact, RecipientChipDescriptor } from './types';

interface RecipientRowProps {
  label: string;
  chips: RecipientChipDescriptor[];
  isAdding: boolean;
  addingValue: string;
  addingError: boolean;
  suggestions: EmailComposerContact[];
  onChangeAdding: (v: string) => void;
  onCommitAdd: () => void;
  onCancelAdd: () => void;
  onClickAdd: () => void;
  onPickSuggestion: (email: string) => void;
  extraAction?: React.ReactNode;
}

export function RecipientRow({
  label,
  chips,
  isAdding,
  addingValue,
  addingError,
  suggestions,
  onChangeAdding,
  onCommitAdd,
  onCancelAdd,
  onClickAdd,
  onPickSuggestion,
  extraAction,
}: RecipientRowProps) {
  const gt = useGT();
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // mousedown inside the suggestion list pre-empts the input's blur-commit so
  // a click reliably runs onPickSuggestion before the input tears down.
  const suppressBlurRef = useRef(false);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [addingValue, isAdding]);

  const showSuggestions = isAdding && suggestions.length > 0;

  return (
    <div className="flex items-start gap-2">
      <span className="w-8 shrink-0 pt-1.5 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="relative flex-1">
        <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-background px-2 py-1.5">
          {chips.map((chip) => (
            <RecipientChip
              key={chip.key}
              email={chip.email}
              onRemove={chip.onRemove}
            />
          ))}
          {isAdding ? (
            <input
              type="email"
              autoFocus
              value={addingValue}
              onChange={(e) => onChangeAdding(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' && showSuggestions) {
                  e.preventDefault();
                  setHighlightedIndex((i) => (i + 1) % suggestions.length);
                } else if (e.key === 'ArrowUp' && showSuggestions) {
                  e.preventDefault();
                  setHighlightedIndex(
                    (i) => (i - 1 + suggestions.length) % suggestions.length,
                  );
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if (showSuggestions && suggestions[highlightedIndex]) {
                    onPickSuggestion(suggestions[highlightedIndex].email);
                  } else {
                    onCommitAdd();
                  }
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelAdd();
                }
              }}
              onBlur={() => {
                if (suppressBlurRef.current) {
                  suppressBlurRef.current = false;
                  return;
                }
                if (addingValue.trim() === '') {
                  onCancelAdd();
                } else {
                  onCommitAdd();
                }
              }}
              placeholder={gt('email@example.com', {
                context: 'email input placeholder',
              })}
              className={`h-6 min-w-[180px] flex-1 border-0 bg-transparent text-sm outline-hidden ${
                addingError ? 'text-red-600' : ''
              }`}
            />
          ) : (
            <>
              {extraAction}
              <button
                type="button"
                onClick={onClickAdd}
                className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted cursor-pointer"
              >
                <PlusIcon className="h-3 w-3" />
                <T context="recipient row, button to open inline email input">
                  Add
                </T>
              </button>
            </>
          )}
        </div>
        {showSuggestions && (
          <ul
            role="listbox"
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-md border bg-popover shadow-md"
          >
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                role="option"
                aria-selected={i === highlightedIndex}
                onMouseDown={() => {
                  suppressBlurRef.current = true;
                }}
                onMouseEnter={() => setHighlightedIndex(i)}
                onClick={() => onPickSuggestion(s.email)}
                className={`flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 text-sm ${
                  i === highlightedIndex ? 'bg-muted' : ''
                }`}
              >
                <span className="truncate">{s.email}</span>
                {s.tags.length > 0 && (
                  <span className="flex shrink-0 flex-wrap gap-1">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      >
                        {t}
                      </span>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
